import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, User, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { blogPosts } from '../data/blogPosts';

const BlogPage = () => {
  return (
    <>
      <Helmet>
        <title>Blog — Searchefy | SEO & Link Building Guides</title>
        <meta
          name="description"
          content="Learn link building strategies, SEO tips, and backlink best practices. Guides for SEO professionals, marketers, and founders."
        />
        <link rel="canonical" href="https://searchefy.com/blog" />
        <meta property="og:title" content="Blog — Searchefy | SEO & Link Building Guides" />
        <meta
          property="og:description"
          content="Learn link building strategies, SEO tips, and backlink best practices."
        />
      </Helmet>

      <div data-testid="blog-page" className="min-h-screen bg-[#F9FAFB]">
        <Navbar />

        <main className="pt-24">
          {/* Hero */}
          <section className="section-dark py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
                  Learn Link Building.{' '}
                  <span className="gradient-text">Get Found Faster.</span>
                </h1>
                <p className="text-white/70 max-w-2xl mx-auto">
                  Guides, strategies, and deep-dives for SEO professionals and founders building their online presence.
                </p>
              </motion.div>
            </div>
          </section>

          {/* Blog Posts Grid */}
          <section className="py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              {/* Featured Post */}
              {blogPosts.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="mb-12"
                >
                  <Link to={`/blog/${blogPosts[0].slug}`} className="group block">
                    <div className="grid md:grid-cols-2 gap-8 bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow">
                      <div className="aspect-video md:aspect-auto bg-gradient-to-br from-blue-100 to-purple-100 relative flex items-center justify-center">
                        <span className="text-8xl opacity-50">{blogPosts[0].emoji}</span>
                        <div className="absolute top-4 left-4">
                          <span className="px-4 py-1.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-medium rounded-full">
                            Featured
                          </span>
                        </div>
                      </div>
                      <div className="p-8 flex flex-col justify-center">
                        <span className="text-sm text-blue-600 font-medium mb-2">
                          {blogPosts[0].category}
                        </span>
                        <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors">
                          {blogPosts[0].title}
                        </h2>
                        <p className="text-gray-600 mb-6">
                          {blogPosts[0].excerpt}
                        </p>
                        <div className="flex items-center gap-6 text-sm text-gray-500">
                          <span className="flex items-center gap-2">
                            <User size={16} />
                            {blogPosts[0].author}
                          </span>
                          <span className="flex items-center gap-2">
                            <Clock size={16} />
                            {blogPosts[0].readTime}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              )}

              {/* Other Posts */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {blogPosts.slice(1).map((post, index) => (
                  <motion.div
                    key={post.slug}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    data-testid={`blog-card-${index + 1}`}
                  >
                    <Link to={`/blog/${post.slug}`} className="group block h-full">
                      <div className="blog-card h-full flex flex-col">
                        <div className="aspect-video bg-gradient-to-br from-blue-100 to-purple-100 relative flex items-center justify-center">
                          <span className="text-5xl opacity-50">{post.emoji}</span>
                          <div className="absolute top-3 left-3">
                            <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-xs font-medium text-gray-700 rounded-full">
                              {post.category}
                            </span>
                          </div>
                        </div>
                        <div className="p-6 flex-1 flex flex-col">
                          <h3 className="font-display text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                            {post.title}
                          </h3>
                          <p className="text-gray-600 text-sm mb-4 flex-1">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center justify-between text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <User size={14} />
                              {post.author}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {post.readTime}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>

              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mt-16 text-center p-12 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl"
              >
                <h3 className="font-display text-2xl font-bold text-gray-900 mb-4">
                  Ready to Start Building Backlinks?
                </h3>
                <p className="text-gray-600 mb-6 max-w-xl mx-auto">
                  Stop reading about link building. Start doing it with a curated directory of 500+ backlink sources.
                </p>
                <a
                  href="https://checkout.dodopayments.com/buy/pdt_0Nc1Sd7RjzAPh38CNHxE9?quantity=1"
                  data-testid="blog-cta"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Get Lifetime Access — $9
                  <ArrowRight size={18} />
                </a>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default BlogPage;
